import React from 'react';
import {
  RhymeConfig,
  StructureConfig,
  RHYME_SCHEME_OPTIONS,
  RHYME_EXACTNESS_OPTIONS,
  RHYME_CADENCE_OPTIONS,
  RHYME_DEVICE_OPTIONS,
  RHYME_METER_OPTIONS,
  STRUCTURE_ARCHITECTURE_OPTIONS,
  STRUCTURE_MODIFIER_OPTIONS,
  STRUCTURE_ENDING_OPTIONS,
} from '@/types';
import { DescriptiveSelect } from './ui/DescriptiveSelect';
import { Badge } from './ui/Badge';
import { Tooltip } from './Tooltip';

/**
 * Rhyme and structure configuration — decomposed per docs/Rhyme-Schemes.md
 * and docs/Song-Structures.md (v2, Forgejo #90). Replaces the old two flat
 * dropdowns (SONG_STRUCTURES/RHYME_SCHEMES), which mixed incompatible axes
 * into single options and mostly fell back to the same handful of defaults.
 *
 * RiffWriter has no section-aware generation pipeline (see
 * src/utils/rhymeStructureInstructions.ts), so this offers real independent
 * scheme choices per section (verse/chorus/bridge) plus song-wide settings
 * for everything else, compiled to plain-English prompt instructions rather
 * than mechanically enforced the way Studio Team's agent pipeline does it.
 */

interface MultiToggleProps {
  options: { value: string; label: string; description: string }[];
  selected: string[];
  onToggle: (value: string) => void;
  max: number;
}

function MultiToggle({ options, selected, onToggle, max }: MultiToggleProps) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((opt) => {
        const isSelected = selected.includes(opt.value);
        const atCap = selected.length >= max && !isSelected;
        return (
          <Tooltip key={opt.value} text={opt.description}>
            <Badge
              onClick={() => { if (!atCap) onToggle(opt.value); }}
              className={`cursor-pointer transition-all text-[11px] ${
                isSelected ? 'shadow-lg scale-105' : atCap ? 'opacity-40 cursor-not-allowed' : 'hover:border-slate-600 hover:text-slate-200'
              }`}
            >
              {opt.label}
            </Badge>
          </Tooltip>
        );
      })}
    </div>
  );
}

interface FieldProps {
  label: string;
  tooltip: string;
  children: React.ReactNode;
}

function Field({ label, tooltip, children }: FieldProps) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-400 mb-1 text-left">
        <Tooltip text={tooltip}>
          <span className="cursor-help border-b border-dashed border-slate-700 hover:border-slate-500 transition-colors">{label}</span>
        </Tooltip>
      </label>
      {children}
    </div>
  );
}

export interface RhymeStructurePickerProps {
  rhyme: RhymeConfig;
  structure: StructureConfig;
  onRhymeChange: (rhyme: RhymeConfig) => void;
  onStructureChange: (structure: StructureConfig) => void;
}

export function RhymeStructurePicker({ rhyme, structure, onRhymeChange, onStructureChange }: RhymeStructurePickerProps) {
  const toggleDevice = (value: string) => {
    const devices = rhyme.devices.includes(value)
      ? rhyme.devices.filter(d => d !== value)
      : [...rhyme.devices, value];
    onRhymeChange({ ...rhyme, devices });
  };

  const toggleModifier = (value: string) => {
    const modifiers = structure.modifiers.includes(value)
      ? structure.modifiers.filter(m => m !== value)
      : [...structure.modifiers, value];
    onStructureChange({ ...structure, modifiers });
  };

  return (
    <div className="space-y-5">
      {/* Structure */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Architecture" tooltip="The song's overall shape — which section returns, when the chorus arrives.">
          <DescriptiveSelect
            value={structure.architecture}
            onChange={(val) => onStructureChange({ ...structure, architecture: val })}
            options={STRUCTURE_ARCHITECTURE_OPTIONS}
            className="bg-slate-950 rounded-xl"
          />
        </Field>
        <Field label="Ending" tooltip="How the song stops — cold cut, fade, false ending, unresolved.">
          <DescriptiveSelect
            value={structure.ending}
            onChange={(val) => onStructureChange({ ...structure, ending: val })}
            options={STRUCTURE_ENDING_OPTIONS}
            className="bg-slate-950 rounded-xl"
          />
        </Field>
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-400 mb-1 text-left">
          <Tooltip text="Bolt-ons that alter the architecture without changing it. Pick up to 2.">
            <span className="cursor-help border-b border-dashed border-slate-700 hover:border-slate-500 transition-colors">Structure Modifiers (max 2)</span>
          </Tooltip>
        </label>
        <MultiToggle options={STRUCTURE_MODIFIER_OPTIONS} selected={structure.modifiers} onToggle={toggleModifier} max={2} />
      </div>

      {/* Rhyme */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="Verse Scheme" tooltip="Which lines rhyme in the verse.">
          <DescriptiveSelect
            value={rhyme.verseScheme}
            onChange={(val) => onRhymeChange({ ...rhyme, verseScheme: val })}
            options={RHYME_SCHEME_OPTIONS}
            className="bg-slate-950 rounded-xl"
          />
        </Field>
        <Field label="Chorus Scheme" tooltip="Which lines rhyme in the chorus — should differ from the verse scheme.">
          <DescriptiveSelect
            value={rhyme.chorusScheme}
            onChange={(val) => onRhymeChange({ ...rhyme, chorusScheme: val })}
            options={RHYME_SCHEME_OPTIONS}
            className="bg-slate-950 rounded-xl"
          />
        </Field>
        <Field label="Bridge Scheme" tooltip="Which lines rhyme in the bridge — Free Verse is the strongest available departure.">
          <DescriptiveSelect
            value={rhyme.bridgeScheme}
            onChange={(val) => onRhymeChange({ ...rhyme, bridgeScheme: val })}
            options={RHYME_SCHEME_OPTIONS}
            className="bg-slate-950 rounded-xl"
          />
        </Field>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="Exactness" tooltip="How close the rhymes sit — perfect, slant, or mixed.">
          <DescriptiveSelect
            value={rhyme.exactness}
            onChange={(val) => onRhymeChange({ ...rhyme, exactness: val })}
            options={RHYME_EXACTNESS_OPTIONS}
            className="bg-slate-950 rounded-xl"
          />
        </Field>
        <Field label="Cadence" tooltip="Where the stress falls on rhymed words — masculine, feminine, or mixed.">
          <DescriptiveSelect
            value={rhyme.cadence}
            onChange={(val) => onRhymeChange({ ...rhyme, cadence: val })}
            options={RHYME_CADENCE_OPTIONS}
            className="bg-slate-950 rounded-xl"
          />
        </Field>
        <Field label="Meter" tooltip="The syllabic pulse — iambic, trochaic, or natural speech rhythm.">
          <DescriptiveSelect
            value={rhyme.meter}
            onChange={(val) => onRhymeChange({ ...rhyme, meter: val })}
            options={RHYME_METER_OPTIONS}
            className="bg-slate-950 rounded-xl"
          />
        </Field>
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-400 mb-1 text-left">
          <Tooltip text="Texture layered on top of the scheme — internal rhyme, assonance, alliteration. Pick up to 2.">
            <span className="cursor-help border-b border-dashed border-slate-700 hover:border-slate-500 transition-colors">Rhyme Devices (max 2)</span>
          </Tooltip>
        </label>
        <MultiToggle options={RHYME_DEVICE_OPTIONS} selected={rhyme.devices} onToggle={toggleDevice} max={2} />
      </div>
    </div>
  );
}
